'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'),
  );
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // Any malformed cookie in the domain would otherwise throw and
    // silently break exposure tracking for the rest of the session.
    return undefined;
  }
}

export default function AbTestTracker() {
  const posthog = usePostHog();
  // The layout mounts once and stays mounted across client-side navigation,
  // so a `[posthog]`-only dependency would only fire on the very first page
  // load. The proxy rewrites `ph_exposure` on every request, so we must
  // re-read it whenever the pathname changes to attribute exposure to each
  // A/B-tested page the visitor actually saw.
  const pathname = usePathname();

  useEffect(() => {
    if (!posthog) return;
    // ph_exposure is set by the proxy with ONLY the flag driving the
    // current page's A/B test (or absent when no test is active here).
    // Reading ph_bootstrap directly would attribute exposures to
    // experiments that live on other pages, polluting their metrics.
    const raw = readCookie('ph_exposure');
    if (!raw) return;
    try {
      const flags = JSON.parse(raw) as Record<string, string | boolean>;
      Object.entries(flags).forEach(([key, value]) => {
        // Explicitly capture $feature_flag_called. posthog-js does not
        // auto-fire this event when flag values come from bootstrap.
        posthog.capture('$feature_flag_called', {
          $feature_flag: key,
          $feature_flag_response: value,
        });
      });
    } catch {
      // Ignore malformed cookie
    }
  }, [posthog, pathname]);

  return null;
}
